import { z } from 'zod'
import { connectorIdSchema } from './connections'
import { itemTypeSchema, TITLE_MAX } from './items'

export const INGEST_KEY_MAX = 300
export const PARTICIPANTS_MAX = 100
export const SOURCE_URL_MAX = 2000
export const SENDER_NAME_MAX = 200

/** The message hugo would answer in, e.g. a Gmail thread and its newest message. */
export const ingestThreadSchema = z.object({
  threadId: z.string().min(1).max(INGEST_KEY_MAX),
  messageId: z.string().min(1).max(INGEST_KEY_MAX),
  subject: z.string().max(TITLE_MAX * 2),
})
export type IngestThread = z.output<typeof ingestThreadSchema>

/**
 * One task-like signal from a connector, normalized (e.g. a new Gmail message). Signals with the
 * same `dedupeKey` (e.g. one per thread) belong to the same item; `externalId` identifies the
 * signal itself (e.g. the message). Everything in here is untrusted data from another system,
 * never instructions, and holds no message bodies.
 */
export const ingestSignalSchema = z.object({
  connector: connectorIdSchema,
  externalId: z.string().min(1).max(INGEST_KEY_MAX),
  dedupeKey: z.string().min(1).max(INGEST_KEY_MAX),
  title: z.string().trim().min(1).max(TITLE_MAX),
  /** Link back to the source. Only http(s): it is rendered as a link. */
  url: z
    .url({ protocol: /^https?$/ })
    .max(SOURCE_URL_MAX)
    .nullable(),
  /**
   * Who sent the signal (e.g. the message's From). Some systems give no address (Google Chat
   * gives a display name only): then nothing can be sent back to them from hugo.
   */
  from: z
    .object({ name: z.string().max(SENDER_NAME_MAX).nullable(), email: z.email().nullable() })
    .refine((f) => f.name !== null || f.email !== null, 'a sender needs a name or an email')
    .nullable(),
  /** Everyone on the signal (e.g. From, To and Cc), the sender included when it has an address. */
  participants: z.array(z.email()).max(PARTICIPANTS_MAX),
  occurredAt: z.iso.datetime(),
  thread: ingestThreadSchema.nullable(),
  /** The kind of work, when the source makes it obvious (an invitation is a meeting). */
  type: itemTypeSchema.nullable().default(null),
  /**
   * The ask was settled in the source: `done` (the invitation was answered, the comment
   * resolved) or `cancelled` (the event was cancelled, the comment deleted). Ingest closes the
   * item while it is still active and never creates an item for a settled signal.
   */
  resolved: z.enum(['done', 'cancelled']).nullable().default(null),
  /**
   * The source makes the ask unambiguous: it is assigned to the user (a review requested, an
   * issue assigned), so a new item is filed as this kind instead of waiting in the inbox.
   */
  file: z.enum(['task', 'owe']).nullable().default(null),
  /** The due date the source gives the ask (an issue's due date), for a new item. */
  due: z.object({ at: z.iso.datetime(), allDay: z.boolean() }).nullable().default(null),
})
export type IngestSignal = z.output<typeof ingestSignalSchema>
export type IngestSignalInput = z.input<typeof ingestSignalSchema>

/** What ingesting a signal did to its item. */
export const ingestOutcomes = ['created', 'updated', 'reopened', 'closed', 'unchanged'] as const
export const ingestOutcomeSchema = z.enum(ingestOutcomes)
export type IngestOutcome = z.output<typeof ingestOutcomeSchema>
